import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { httpAuthTypes, httpCertificateLocationTypes, HttpServerLinkedService } from 'app/model/linkedservice/http-server.linkedservice';
import { Metamodel } from 'app/model/base/model';
import { HttpFileCopySource } from 'app/model/pipeline/copysource/http-file.copysource';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { getAuthHeader, maxConcurrentConnectionsMetadata, cdcDestination } from '../consts/metadata-consts';
import { Constants } from 'app/shared/shared-constants';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, Type } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const dynamicContentControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'url',
            label: stringResources.ModelName_BaseUrl,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
    },
    {
        type: 'radio',
        bind: 'enableServerCertificateValidation',
        label: stringResources.ModelName_EnableServerCertificateValidation,
        description: stringResources.HttpEnableServerCertificateValidationDescription,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.Enable,
                value: true
            },
            {
                label: stringResources.Disable,
                value: false
            }
        ],
        defaultValue: true
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        description: stringResources.HttpAuthenticationTypeDescription,
        options: [
            {
                label: stringResources.Anonymous_DisplayText,
                value: httpAuthTypes.Anonymous
            },
            {
                label: stringResources.Basic_DisplayText,
                value: httpAuthTypes.Basic
            },
            {
                label: stringResources.Digest_DisplayText,
                value: httpAuthTypes.Digest
            },
            {
                label: stringResources.Windows_DisplayText,
                value: httpAuthTypes.Windows
            },
            {
                label: stringResources.ClientCertificate_DisplayText,
                value: httpAuthTypes.ClientCertificate
            }
        ],
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: 'Basic',
        cases: [
            {
                caseValue: httpAuthTypes.Anonymous,
                children: [
                    getAuthHeader(true)
                ]
            },
            {
                caseValue: httpAuthTypes.Basic,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.HttpServer_DisplayText),
                            stringResources.ModelName_Password)
                    },
                    getAuthHeader(false)
                ]
            },
            {
                caseValue: httpAuthTypes.Digest,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {

                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.HttpServer_DisplayText),
                            stringResources.ModelName_Password)
                    },
                    getAuthHeader(true)
                ]
            },
            {
                caseValue: httpAuthTypes.Windows,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.HttpServer_DisplayText),
                            stringResources.ModelName_Password)
                    },
                    getAuthHeader(true)
                ]
            },
            {
                caseValue: httpAuthTypes.ClientCertificate,
                children: [
                    {
                        type: 'select-switch',
                        bind: 'certificateLocationType',
                        label: stringResources.HTTPServer_LinkedService_CertificateLocationTypes,
                        options: [
                            {
                                label: stringResources.HTTPServer_LinkedService_UseEmbeddedCertificateData,
                                value: httpCertificateLocationTypes.EmbeddedCertificateData
                            },
                            {
                                label: stringResources.HTTPServer_LinkedService_UseCertificateThumbprint,
                                value: httpCertificateLocationTypes.CertificateThumbprint
                            }
                        ],
                        isLabelVertical: true,
                        defaultValue: httpCertificateLocationTypes.EmbeddedCertificateData,
                        cases: [
                            {
                                caseValue: httpCertificateLocationTypes.EmbeddedCertificateData,
                                children: [
                                    {
                                        type: 'key-file-content',
                                        bind: 'embeddedCertData',
                                        label: stringResources.ModelName_EmbeddedCertificateData,
                                        required: true,
                                        validators: [
                                            { type: 'required', errorMessage: '' }
                                        ],
                                        isLabelVertical: true,
                                        isDisabled: true,
                                        allowFileString: '.pfx'
                                    },
                                    {
                                        type: 'dynamic-akv-switcher',
                                        enableAKVParameterization: true,
                                        bind: 'password',
                                        label: stringResources.ModelName_CertificatePassword,
                                        child: {
                                            type: 'parameterizable',
                                            attribute: 'password',
                                            disableAddParameter: true,
                                            child: {
                                                type: 'password',
                                                label: stringResources.ModelName_CertificatePassword,
                                                isLabelVertical: true
                                            }
                                        },
                                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.HttpServer_DisplayText),
                                            stringResources.ModelName_CertificatePassword)
                                    }
                                ]
                            }, {
                                caseValue: httpCertificateLocationTypes.CertificateThumbprint,
                                children: [
                                    {
                                        type: 'parameterizable',
                                        child: {
                                            type: 'textbox',
                                            bind: 'certThumbprint',
                                            label: stringResources.ModelName_CertificateThumbprint,
                                            required: true,
                                            validators: [
                                                { type: 'required', errorMessage: '' }
                                            ],
                                            isLabelVertical: true
                                        }
                                    }
                                ]
                            }
                        ]
                    },
                    getAuthHeader(true)
                ]
            }
        ]
    }
];

export const httpServerMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(HttpServerLinkedService).name,
        displayName: stringResources.HttpServer_DisplayText,
        linkedServiceKind: 'file',
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'HttpServer' as ResourceKind
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic, ConnectionAuthKind.Anonymous]
            .concat(GlobalVariables.enableWebConnection ? [ConnectionAuthKind.OAuth2, ConnectionAuthKind.ServicePrincipal] : [])
            .concat(!GlobalVariables.enableWebConnection && GlobalVariables.enableUploadFile ? [ConnectionAuthKind.OAuth2] : []),
        icon: {
            className: 'svg-i-100x100-HTTP'
        },
        isFirstClass: true,
        notSupportCopySink: true,
        copySinkTypes: [],
        copySourceTypes: [Metamodel.modelFor(HttpFileCopySource).name],
        supportCopyJobSource: true,
        ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        category: ['GenericProtocol'],
        supportRootFolder: true,
        supportDatasetV2: true,
        supportOPDG: GlobalVariables.enableOPDG,
        queryReadPropertieControls: [
            {
                type: 'select',
                bind: 'requestMethod',
                label: stringResources.Dataset_RequestMethod,
                options: [
                    {
                        label: stringResources.Dataset_RequestMethod_Get,
                        value: 'GET'
                    },
                    {
                        label: stringResources.Method_Post,
                        value: 'POST'
                    }
                ],
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                isLabelVertical: true,
                defaultValue: 'GET',
                description: stringResources.Dataset_RequestMethodDescription
            },
            {
                type: 'textarea',
                bind: 'additionalHeaders',
                label: stringResources.Dataset_AdditionalHeaders,
                isLabelVertical: true,
                description: stringResources.Dataset_AdditionalHeadersDesc_textarea
            },
            {
                type: 'textarea',
                bind: 'requestBody',
                label: stringResources.Dataset_RequestBody,
                isLabelVertical: true,
                description: stringResources.Dataset_RequestBodyDescription
            }
        ],
        storeReadSettingControls: [
            {
                type: 'parameterizable',
                child: {
                    type: 'select',
                    bind: 'requestMethod',
                    label: stringResources.Dataset_RequestMethod,
                    options: [
                        {
                            label: stringResources.Dataset_RequestMethod_Get,
                            value: 'GET'
                        },
                        {
                            label: stringResources.Method_Post,
                            value: 'POST'
                        }
                    ],
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    defaultValue: 'GET',
                    description: stringResources.Dataset_RequestMethodDescription
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textarea',
                    bind: 'additionalHeaders',
                    label: stringResources.Dataset_AdditionalHeaders,
                    description: stringResources.Dataset_AdditionalHeadersDesc_textarea
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textarea',
                    bind: 'requestBody',
                    label: stringResources.Dataset_RequestBody,
                    description: stringResources.Dataset_RequestBodyDescription
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'requestTimeout',
                    label: stringResources.ModelName_Request_Timeout,
                    validators: [
                        {
                            type: 'pattern',
                            pattern: Constants.timeoutValidationPattern,
                            errorMessage: format(stringResources.InvalidTimeoutPattern, stringResources.ModelName_Request_Timeout)
                        }
                    ],
                    description: stringResources.Dataset_HttpRequestTimeoutDescription
                },
                dynamicContentTextClass: 'input-spaced'
            },
            maxConcurrentConnectionsMetadata
        ],
        supportFirstClassParameterExperience: true,
        controls: dynamicContentControls,
        additionalPropertiesInFabric: GlobalVariables.disableServerCertificate ? [
            {
                label: 'Disable server certificate validation',
                value: 'disableServerCertificateValidation',
                type: Type.Boolean
            }
        ] : undefined
    };
    return metadata;
};
